import { createCsvBlob, repairCsvText } from './csvEncoding'

const formatValue = (val) => {
  if (val === null || val === undefined || val === '') return ''
  if (typeof val === 'number') return val.toString()
  if (typeof val === 'object') return JSON.stringify(val)

  const v = String(val).trim()

  if (/^\d{4}-\d{2}-\d{2}/.test(v)) {
    try {
      const d = new Date(v)

      if (Number.isNaN(d.getTime())) return v

      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()

      return `${day}.${month}.${year}`
    } catch {
      return v
    }
  }

  return v
}

const pad = (n) => String(n).padStart(2, '0')

const fileStamp = () => {
  const d = new Date()
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
}

const downloadBlob = (blob, name) => {
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = name
  a.click()
  URL.revokeObjectURL(a.href)
}

const csvEscape = (value) => `"${repairCsvText(formatValue(value))}"`

const arrayBufferToBase64 = (buffer) => {
  let binary = ''
  const bytes = new Uint8Array(buffer)

  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i])
  }

  return window.btoa(binary)
}

const loadPdfFont = async (doc, url, fileName, fontName, fontStyle) => {
  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(`PDF font not found: ${url}`)
  }

  const buffer = await response.arrayBuffer()
  const base64 = arrayBufferToBase64(buffer)

  doc.addFileToVFS(fileName, base64)
  doc.addFont(fileName, fontName, fontStyle)
}

const ensureReceiptPdfFonts = async (doc) => {
  await loadPdfFont(
    doc,
    '/fonts/Roboto-Regular.ttf',
    'Roboto-Regular.ttf',
    'Roboto',
    'normal'
  )

  await loadPdfFont(
    doc,
    '/fonts/Roboto-Bold.ttf',
    'Roboto-Bold.ttf',
    'Roboto',
    'bold'
  )
}

const translateReceiptHeader = (h, t) => {
  const map = {
    vehicleId: 'TRIPLOG.RECEIPTS.TABLE.VEHICLE',
    date: 'TRIPLOG.RECEIPTS.TABLE.DATE',
fuel_type: 'TRIPLOG.RECEIPTS.TABLE.FUEL_TYPE',
charging_type: 'TRIPLOG.RECEIPTS.TABLE.CHARGING_TYPE',
    fuel_name: 'TRIPLOG.RECEIPTS.TABLE.FUEL_NAME',
    unit_price: 'TRIPLOG.RECEIPTS.TABLE.UNIT_PRICE',
    units: 'TRIPLOG.RECEIPTS.TABLE.UNITS',
    unit: 'TRIPLOG.RECEIPTS.TABLE.UNIT',
    total_price: 'TRIPLOG.RECEIPTS.TABLE.TOTAL_PRICE',
    currency: 'TRIPLOG.RECEIPTS.TABLE.CURRENCY',
    receipt_number: 'TRIPLOG.RECEIPTS.TABLE.RECEIPT_NUMBER',
    uid: 'TRIPLOG.RECEIPTS.TABLE.UID',
    payment_type: 'TRIPLOG.RECEIPTS.TABLE.PAYMENT_TYPE',
    vat_rate: 'TRIPLOG.RECEIPTS.TABLE.VAT_RATE',
    transaction_id: 'TRIPLOG.RECEIPTS.TABLE.TRANSACTION_ID',
    store_name: 'TRIPLOG.RECEIPTS.TABLE.STORE_NAME',
    store_address: 'TRIPLOG.RECEIPTS.TABLE.STORE_ADDRESS',
    ico: 'TRIPLOG.RECEIPTS.TABLE.ICO',
    dic: 'TRIPLOG.RECEIPTS.TABLE.DIC',
    note: 'TRIPLOG.RECEIPTS.TABLE.NOTES'
  }

  if (h === 'spz') return t('vehicle_license_plate')

  const key = map[h]

  if (key) return t(key)

  return h
}

export const buildReceiptExportRecords = (receipts, vehiclesById, t) => {
  return receipts.map((r) => {
    const vehicle = vehiclesById[String(r?.vehicleId)] || r?.vehicle

    const vehicleName =
      vehicle?.name ||
      vehicle?.model ||
      vehicle?.licensePlate ||
      vehicle?.spz ||
      ''

    const plate = vehicle?.licensePlate || vehicle?.spz || ''

return {
  date: r?.date,
  vehicleId: vehicleName,
  spz: plate,

  fuel_type:
    r?.fuel_type === 'electric'
      ? t('TRIPLOG.RECEIPTS.TABLE.ELECTRIC')
      : r?.fuel_type === 'fuel'
        ? t('TRIPLOG.RECEIPTS.TABLE.FUEL')
        : '',

  charging_type:
    r?.fuel_type === 'electric'
      ? r?.charging_type === 'public'
        ? t('TRIPLOG.RECEIPTS.TABLE.CHARGING_PUBLIC')
        : r?.charging_type === 'home'
          ? t('TRIPLOG.RECEIPTS.TABLE.CHARGING_HOME')
          : r?.charging_type === 'package'
            ? t('TRIPLOG.RECEIPTS.TABLE.CHARGING_PACKAGE')
            : ''
      : '',

  fuel_name: r?.fuel_name ?? '',
      unit_price: r?.unit_price ?? '',
      units: r?.units ?? '',
      unit: r?.unit ?? '',
      total_price: r?.total_price ?? '',
      currency: r?.currency ?? '',
      vat_rate: r?.vat_rate ?? '',
      receipt_number: r?.receipt_number ?? '',
      uid: r?.uid ?? '',
      payment_type: r?.payment_type ?? '',
      transaction_id: r?.transaction_id ?? '',
      store_name: r?.store_name ?? '',
      store_address: r?.store_address ?? '',
      ico: r?.ico ?? '',
      dic: r?.dic ?? '',
      note: r?.note ?? ''
    }
  })
}

export const exportReceiptsCSV = (records, headers, t, periodText, vehicleText) => {
  const header = headers.map(h => translateReceiptHeader(h, t)).join(';')

  const rows = records.map(r =>
    headers.map(h => csvEscape(r[h])).join(';')
  )

  const csv = [
    `${t('TRIPLOG.STATISTICS.SHOWN_PERIOD')};"${repairCsvText(periodText)}"`,
    `${t('TRIPLOG.STATISTICS.SHOWN_VEHICLE')};"${repairCsvText(vehicleText)}"`,
    '',
    header,
    ...rows
  ].join('\n')

  downloadBlob(
    createCsvBlob(csv),
    `receipts_${fileStamp()}.csv`
  )
}

export const exportReceiptsXLS = async (records, headers, t, periodText, vehicleText) => {
  const XLSX = await import('xlsx-js-style')

  const titleRows = [
    [t('TRIPLOG.STATISTICS.SHOWN_PERIOD'), periodText],
    [t('TRIPLOG.STATISTICS.SHOWN_VEHICLE'), vehicleText],
    []
  ]

  const translatedHeaders = headers.map(h => translateReceiptHeader(h, t))
  const dataRows = records.map(r => headers.map(h => formatValue(r[h]) ?? ''))

  const worksheetData = [...titleRows, translatedHeaders, ...dataRows]

  const ws = XLSX.utils.aoa_to_sheet(worksheetData)

  ws['!cols'] = headers.map(() => ({ wch: 18 }))

  const headerRowIndex = titleRows.length

  translatedHeaders.forEach((_, colIndex) => {
    const cellAddress = XLSX.utils.encode_cell({
      r: headerRowIndex,
      c: colIndex
    })

    if (!ws[cellAddress]) return

    ws[cellAddress].s = {
      font: {
        name: 'Arial',
        sz: 10,
        bold: true
      },
      fill: {
        fgColor: { rgb: 'F0F0F0' }
      },
      alignment: {
        horizontal: 'left',
        vertical: 'center'
      }
    }
  })

  titleRows.forEach((row, rowIndex) => {
    row.forEach((_, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({
        r: rowIndex,
        c: colIndex
      })

      if (!ws[cellAddress]) return

      ws[cellAddress].s = {
        font: {
          name: 'Arial',
          sz: 10,
          bold: true
        },
        alignment: {
          horizontal: 'left',
          vertical: 'center'
        }
      }
    })
  })

  dataRows.forEach((row, rowIndex) => {
    row.forEach((_, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({
        r: headerRowIndex + 1 + rowIndex,
        c: colIndex
      })

      if (!ws[cellAddress]) return

      ws[cellAddress].s = {
        font: {
          name: 'Arial',
          sz: 10
        },
        alignment: {
          horizontal: 'left',
          vertical: 'center'
        }
      }
    })
  })

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Receipts')

  XLSX.writeFile(wb, `receipts_${fileStamp()}.xlsx`)
}

export const exportReceiptsPDF = async (records, headers, t, periodText, vehicleText) => {
  const { jsPDF } = await import('jspdf')
  const autoTableModule = await import('jspdf-autotable')
  const autoTable = autoTableModule.default || autoTableModule.autoTable

  const translatedHeaders = headers.map(h => translateReceiptHeader(h, t))
  const dataRows = records.map(r => headers.map(h => formatValue(r[h]) ?? ''))

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'a4'
  })

  await ensureReceiptPdfFonts(doc)

  const marginLeft = 18
  const marginRight = 18
  const pageWidth = doc.internal.pageSize.getWidth()
  const usableWidth = pageWidth - marginLeft - marginRight
  const columnWidth = usableWidth / headers.length

  doc.setFont('Roboto', 'bold')
  doc.setFontSize(14)
  doc.text(t('TRIPLOG.TAB.RECEIPTS'), marginLeft, 28)

  doc.setFontSize(10)
  doc.text(
    `${t('TRIPLOG.STATISTICS.SHOWN_PERIOD')}: ${periodText}`,
    marginLeft,
    44
  )

  doc.setFontSize(9)
  doc.text(
    `${t('TRIPLOG.STATISTICS.SHOWN_VEHICLE', { defaultValue: 'Zobrazené vozidlo' })}: ${vehicleText}`,
    marginLeft,
    58
  )

  autoTable(doc, {
    head: [translatedHeaders],
    body: dataRows,
    startY: 72,
    theme: 'grid',
    margin: {
      top: 18,
      right: marginRight,
      bottom: 18,
      left: marginLeft
    },
    tableWidth: usableWidth,
    styles: {
      font: 'Roboto',
      fontStyle: 'normal',
      fontSize: 6,
      cellPadding: 2,
      overflow: 'linebreak',
      valign: 'middle',
      halign: 'left',
      lineWidth: 0.3,
      lineColor: [0, 0, 0],
      minCellHeight: 10
    },
    headStyles: {
      font: 'Roboto',
      fontStyle: 'bold',
      fontSize: 6,
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      lineWidth: 0.4,
      lineColor: [0, 0, 0]
    },
    bodyStyles: {
      font: 'Roboto',
      fontStyle: 'normal',
      textColor: [0, 0, 0],
      lineWidth: 0.3,
      lineColor: [0, 0, 0]
    },
    columnStyles: headers.reduce((acc, _header, index) => {
      acc[index] = {
        cellWidth: columnWidth
      }

      return acc
    }, {}),
    horizontalPageBreak: false,
    pageBreak: 'auto',
    rowPageBreak: 'avoid'
  })

  downloadBlob(doc.output('blob'), `receipts_${fileStamp()}.pdf`)
}

export const exportReceiptsXML = (records, headers) => {
  const rows = records
    .map(r => {
      const fields = headers
        .map(h => `<${h}>${formatValue(r[h])}</${h}>`)
        .join('')
      return `<receipt>${fields}</receipt>`
    })
    .join('')

  const xml = `<?xml version="1.0" encoding="UTF-8"?><receipts>${rows}</receipts>`

  downloadBlob(
    new Blob([xml], { type: 'application/xml;charset=utf-8;' }),
    `receipts_${fileStamp()}.xml`
  )
}

export const exportReceiptsICS = (records) => {
  const events = records
    .map(r => {
      const date = r?.date
        ? new Date(r.date).toLocaleString('sv-SE').replace(/[-:T\s]/g, '')
        : ''

      return `BEGIN:VEVENT
SUMMARY:Receipt
DTSTART:${date}
DESCRIPTION:${r.store_name ?? ''}
END:VEVENT`
    })
    .join('\n')

  const ics = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Receipts//EN
${events}
END:VCALENDAR`

  downloadBlob(
    new Blob([ics], { type: 'text/calendar;charset=utf-8;' }),
    `receipts_${fileStamp()}.ics`
  )
}

export const exportReceiptsJSON = (records) => {
  downloadBlob(
    new Blob([JSON.stringify(records, null, 2)], {
      type: 'application/json;charset=utf-8;'
    }),
    `receipts_${fileStamp()}.json`
  )
}